import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Award, Recycle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const conditionLabels = {
  new_aftermarket: { label: 'New', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  used_oem: { label: 'Used OEM', color: 'bg-amber-100 text-amber-700', icon: Recycle },
  refurbished: { label: 'Refurbished', color: 'bg-blue-100 text-blue-700', icon: Award },
};

export default function ProductCard({ product }) {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const discount = product.original_price ? Math.round((1 - product.price / product.original_price) * 100) : 0;
  const cond = conditionLabels[product.condition] || conditionLabels.new_aftermarket;
  const CondIcon = cond.icon;

  const handleAddToCart = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { window.location.href = '/login'; return; }
    try { await addToCart(product.id); } catch {}
  };

  return (
    <Link to={`/products/${product.id}`} className="group bg-white border border-gray-200 rounded hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full overflow-hidden" data-testid={`product-card-${product.id}`}>
      <div className="relative aspect-[4/3] bg-gray-50 overflow-hidden">
        <img src={product.image_url || 'https://placehold.co/400x300/f3f4f6/9ca3af?text=Auto+Part'} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" onError={e => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x300/f3f4f6/9ca3af?text=Auto+Part'; }} />
        {discount > 0 && <span className="absolute top-2 left-2 bg-[#DC2626] text-white text-xs font-bold px-2 py-1 rounded">-{discount}%</span>}
        <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1 ${cond.color}`}><CondIcon size={10} /> {cond.label}</span>
        {product.capa_certified && <span className="absolute bottom-2 left-2 bg-[#0F766E] text-white text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1"><Award size={10} /> CAPA</span>}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-xs text-[#0F766E] font-semibold uppercase tracking-wider mb-1">{product.brand}</p>
        <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 leading-snug" style={{ fontFamily: "'Chivo', sans-serif" }}>{product.name}</h3>
        {product.oem_part_number && <p className="text-[10px] text-gray-400 mb-1">OEM: {product.oem_part_number}</p>}
        {product.quality_grade && <p className="text-[10px] font-semibold text-amber-600 mb-1">{product.quality_grade} {product.mileage ? `| ${(product.mileage/1000).toFixed(0)}K mi` : ''}</p>}
        <div className="mt-auto">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-xl font-black text-[#0F766E]">${product.price.toFixed(2)}</span>
            {product.original_price && <span className="text-sm text-gray-400 line-through">${product.original_price.toFixed(2)}</span>}
          </div>
          {product.warranty && <p className="text-[10px] text-gray-500 mb-2">{product.warranty}</p>}
          <button onClick={handleAddToCart} className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold py-2.5 rounded transition-all active:scale-95 flex items-center justify-center gap-2" data-testid={`add-to-cart-${product.id}`}><ShoppingCart size={16} /> Add to Cart</button>
        </div>
      </div>
    </Link>
  );
}
